import { reaction } from 'mobx';
import { Instance, SnapshotOut } from 'mobx-state-tree';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../npaper';
import {
  GenericModelWithCRUDType,
  ICacheResults,
  IPaginationFilter,
  IQueryCache,
  modelWithCRUD,
} from './modelWithCRUD';

export const useFilterFetch = <S extends GenericModelWithCRUDType>(
  store: Instance<ReturnType<typeof modelWithCRUD>>,
  defaultFilter: IPaginationFilter<SnapshotOut<S>> = {},
  autoFetch = true
) => {
  const toaster = useToast();

  // Use currentKey to track fetches - this ensures that we only update our state if we are the current fetch.
  const currentKey = useRef(0);

  const [, setInternalState] = useState(0);

  const [filter, setFilter] =
    useState<IPaginationFilter<SnapshotOut<S>>>(defaultFilter);
  const [needsMore, _setNeedsMore] = useState(autoFetch);
  const [cache, setCache] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);

  const reset = useCallback((newFilter: typeof defaultFilter) => {
    setFilter(newFilter);
    setCache(undefined);
    _setNeedsMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleError = useCallback(
    (error: any) => {
      _setNeedsMore(false);
      setIsLoading(false);

      if (error.response) {
        toaster.showDefaultError(error);
      } else {
        throw error;
      }
    },
    [toaster]
  );

  useEffect(() => {
    let isSub = true;
    setIsLoading(true);
    store
      .fetchMore(filter)
      .then((cache) => {
        if (isSub) {
          setCache(cache);
          _setNeedsMore(false);
          setIsLoading(false);
        }
      })
      .catch(handleError);
    return () => {
      isSub = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, handleError]);

  const setNeedsMore = (flag: boolean) => {
    if (!isLoading) {
      _setNeedsMore(flag);
    }
  };

  useEffect(() => {
    currentKey.current = 0;
    return () => {
      // Fetches can never have a fetch key of 0.
      currentKey.current = 0;
    };
  }, []);

  const fetchMore = useCallback(() => {
    if (cache && !cache.hasMore) return;
    const key = currentKey.current + 1;
    currentKey.current = key;
    setIsLoading(true);
    store
      .fetchMore(filter)
      .then((cache) => {
        if (currentKey.current === key) {
          setCache(cache);
          _setNeedsMore(false);
          setIsLoading(false);
        }
      })
      .catch(handleError);
    return cache;
  }, [cache, filter, handleError, store]);

  useEffect(() => {
    if (cache && !cache.hasMore) return;
    if (isLoading) return;

    setIsLoading(true);
    fetchMore();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsMore]);

  useEffect(() => {
    if (!cache) return;
    const disposer = reaction(
      () => cache.items.length,
      () => {
        setInternalState((s) => s + 1);
      }
    );
    return () => {
      disposer();
    };
  }, [cache]);

  const res = cache ? (cache.results as ICacheResults<S>) : undefined;

  return {
    reset,
    filter,
    setFilter,
    setNeedsMore,
    fetchMore,
    items: res ? res.items : undefined,
    hasMore: res ? res.hasMore : true,
    total: res ? res.total : 0,
    sums: cache ? (cache as IQueryCache<S>).sums : undefined,
    isLoading,
  };
};
